import { z } from 'zod';

/**
 * A conversation's side panel as the user left it: the conversation's own, saved with it so that it
 * comes back exactly (its tabs in order, the one shown, what each app tab shows, its size), on this
 * device after a restart and on the user's others (`GET`/`PUT /api/conversations/:id/panel`).
 */
const panelTabSchema = z.object({
  id: z.string().min(1).max(128),
  view: z.string().min(1).max(512),
});

const panelSplitSchema: z.ZodType<PanelSplit> = z.lazy(() =>
  z.union([
    z.object({ kind: z.literal('pane'), id: z.string().min(1).max(128) }),
    z.object({
      kind: z.literal('split'),
      id: z.string().min(1).max(128),
      orientation: z.enum(['horizontal', 'vertical']),
      first: panelSplitSchema,
      second: panelSplitSchema,
    }),
  ]),
);
export type PanelSplit =
  | { kind: 'pane'; id: string }
  | {
      kind: 'split';
      id: string;
      orientation: 'horizontal' | 'vertical';
      first: PanelSplit;
      second: PanelSplit;
    };

export const conversationPanelSchema = z.object({
  open: z.boolean(),
  tabs: z.object({
    tabs: z.array(panelTabSchema).max(100),
    activeId: z.string().max(128).nullable(),
    recent: z.array(z.string().max(128)).max(100),
  }),
  size: z.number().min(0).max(100),
  restoreSize: z.number().min(0).max(100),
  /** What each app tab shows: its item, the sidebar section it is of, its view's route. */
  appTargets: z
    .record(
      z.string().max(128),
      z.object({
        item: z.string().max(256).nullable(),
        section: z.string().max(64).optional(),
        route: z.string().max(1024).optional(),
      }),
    )
    .refine((targets) => Object.keys(targets).length <= 100, 'Too many app tabs.'),
  selectedSubagentId: z.string().max(128).nullable(),
  split: panelSplitSchema.nullable(),
  /** When it last changed (milliseconds since the epoch): the newer of two copies wins. */
  updatedAt: z.number().int().nonnegative(),
});
export type ConversationPanel = z.infer<typeof conversationPanelSchema>;

/** `GET /api/conversations/:id/panel`: the panel saved with it, if any. */
export const conversationPanelResponseSchema = z.object({
  panel: conversationPanelSchema.nullable(),
});
export type ConversationPanelResponse = z.infer<typeof conversationPanelResponseSchema>;
